import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $insertTableRowAtSelection,
  $insertTableColumnAtSelection,
  $isTableCellNode,
  $isTableNode,
} from '@lexical/table';
import {
  $getNearestNodeFromDOMNode,
  $setSelection,
} from 'lexical';

const BUTTON_OFFSET_PX = 4;

function TableHoverActionsContainer({ anchorElem }) {
  const [editor] = useLexicalComposerContext();
  const [tableNode, setTableNode] = useState(null);
  const addRowBtnRef = useRef(null);
  const addColBtnRef = useRef(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [showAddCol, setShowAddCol] = useState(false);
  const [rowBtnPos, setRowBtnPos] = useState({ top: 0, left: 0 });
  const [colBtnPos, setColBtnPos] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);

  const resetState = useCallback(() => {
    setShowAddRow(false);
    setShowAddCol(false);
    setTableNode(null);
  }, []);

  useEffect(() => {
    const onMouseMove = (event) => {
      const { target } = event;
      if (!target || !(target instanceof HTMLElement)) {
        return;
      }

      // Find closest table cell (td or th)
      const cell = target.closest('td, th');
      if (!cell) {
        if (timeoutRef.current === null) {
          timeoutRef.current = setTimeout(() => {
            resetState();
            timeoutRef.current = null;
          }, 300);
        }
        return;
      }

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const table = cell.closest('table');
      if (!table) {
        resetState();
        return;
      }

      // Get the table node from the DOM
      let lexicalTableNode = null;
      editor.read(() => {
        const node = $getNearestNodeFromDOMNode(table);
        if (node && $isTableNode(node)) {
          lexicalTableNode = node;
        }
      });

      if (!lexicalTableNode) {
        resetState();
        return;
      }

      setTableNode(lexicalTableNode);

      const tableRect = table.getBoundingClientRect();
      const anchorRect = anchorElem.getBoundingClientRect();

      // Show add-row button at the bottom of the table when hovering near bottom  
      const distToBottom = tableRect.bottom - event.clientY;
      const distToRight = tableRect.right - event.clientX;
      const edgeThreshold = 30;

      // Add row button — shown at the bottom of the table
      if (distToBottom < edgeThreshold && distToBottom >= 0) {
        setShowAddRow(true);
        setRowBtnPos({
          top: tableRect.bottom - anchorRect.top + BUTTON_OFFSET_PX,
          left: tableRect.left - anchorRect.left,
          width: tableRect.width,
        });
      } else {
        setShowAddRow(false);
      }

      // Add column button — shown at the right side of the table
      if (distToRight < edgeThreshold && distToRight >= 0) {
        setShowAddCol(true);
        setColBtnPos({
          top: tableRect.top - anchorRect.top,
          left: tableRect.right - anchorRect.left + BUTTON_OFFSET_PX,
          height: tableRect.height,
        });
      } else {
        setShowAddCol(false);
      }
    };

    const root = editor.getRootElement();
    if (root) {
      root.addEventListener('mousemove', onMouseMove);
      return () => {
        root.removeEventListener('mousemove', onMouseMove);
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [editor, anchorElem, resetState]);

  const addRowAfterLast = useCallback(() => {
    if (!tableNode) return;
    editor.update(() => {
      const table = tableNode.getLatest();
      if (!$isTableNode(table)) return;
      const lastRow = table.getLastChild();
      if (!lastRow) return;
      const lastCell = lastRow.getLastChild();
      if (!lastCell || !$isTableCellNode(lastCell)) return;
      lastCell.selectEnd();
      $insertTableRowAtSelection(true);
      $setSelection(null);
    });
  }, [editor, tableNode]);

  const addColAfterLast = useCallback(() => {
    if (!tableNode) return;
    editor.update(() => {
      const table = tableNode.getLatest();
      if (!$isTableNode(table)) return;
      const firstRow = table.getFirstChild();
      if (!firstRow) return;
      const lastCell = firstRow.getLastChild();
      if (!lastCell || !$isTableCellNode(lastCell)) return;
      lastCell.selectEnd();
      $insertTableColumnAtSelection(true);
      $setSelection(null);
    });
  }, [editor, tableNode]);

  return (
    <>
      {showAddRow && (
        <button
          className="table-hover-add-row-btn"
          ref={addRowBtnRef}
          onClick={addRowAfterLast}
          style={{
            position: 'absolute',
            top: rowBtnPos.top,
            left: rowBtnPos.left,
            width: rowBtnPos.width,
          }}
          title="Add row"
          type="button"
        />
      )}
      {showAddCol && (
        <button
          className="table-hover-add-col-btn"
          ref={addColBtnRef}
          onClick={addColAfterLast}
          style={{
            position: 'absolute',
            top: colBtnPos.top,
            left: colBtnPos.left,
            height: colBtnPos.height,
          }}
          title="Add column"
          type="button"
        />
      )}
    </>
  );
}

export default function TableHoverActionsPlugin({ anchorElem = document.body }) {
  const isEditable = useLexicalEditable();
  return createPortal(
    isEditable ? <TableHoverActionsContainer anchorElem={anchorElem} /> : null,
    anchorElem,
  );
}
